import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import '../../../../types'

import {
	Badge,
	Button,
	Divider,
	Form,
	FormInstance,
	Input,
	InputNumber,
	Select,
	Switch,
	Tooltip,
	Typography,
	notification,
	DatePicker,
} from 'antd';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faArrowUpRightFromSquare,
	faCircleExclamation,
	faCloudCheck,
	faFolderImage,
	faTrash,
} from '@fortawesome/pro-duotone-svg-icons';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';

import { txt } from '../../../../locales/i18n';
import { ImageCatalogue } from '../../../shared/cms/image-catalogue';
import { DeleteModal } from '../../../shared/delete-modal';

import * as ImageService from '../../../../services/bb-api/oplevalt/image';
import * as ExperienceService from '../../../../services/bb-api/oplevalt/experience';
import * as OplevaltContext from '../context';

const newExperienceSymbol = '$';

export const ExperiencePage = () => {
	const [isLoading, setIsLoading] = useState(true);
	const [experienceTitle, setExperienceTitle] = useState('');
	const [openHeaderImageCatalogue, setOpenHeaderImageCatalogue] = useState(false);
	const [openImageImageCatalogue, setOpenImageImageCatalogue] = useState(false);
	const [openAddOnImageCatalogue, setOpenAddOnImageCatalogue] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [formChanged, setFormChanged] = useState(false);
	const [categories, setCategories] = useState<{id: number, name: string}[]>([])
	const [provinces, setProvinces] = useState<{id: number, name: string}[]>([])

	const formRef = useRef<FormInstance>(null);
	const history = useHistory();
	const params = useParams<{ experienceId: string }>();
	const experienceId = params.experienceId;

	useEffect(() => {

		const getResources = async () => {
			try {
				const categoriesList = await ExperienceService.getCategories()
				setCategories(categoriesList)

				const provinceList = await ExperienceService.getProvinces()
				setProvinces(provinceList)
			} catch (e) {
				const message = txt('global.unknown error');
				notification.error({ message });
			}

			if (experienceId === newExperienceSymbol) return setIsLoading(false);

			try {
				const experience = await ExperienceService.get(Number(experienceId));
				setExperienceTitle(experience.title);

				const form = formRef.current;
				if (form === null) return;

				form?.setFieldsValue(experience);
			} catch (error) {
				const message = txt('global.not found');
				notification.error({ message });
				history.goBack();
			}

			setIsLoading(false);
		}

		getResources()
	}, [experienceId, history]);

	useEffect(() => {
		document.onkeydown = async (event: KeyboardEvent) => {
			if ((event.metaKey || event.ctrlKey) && event.key === 's') {
				event.preventDefault();

				const form = formRef.current;
				form?.submit();
			}
		};

		return () => {
			document.onkeydown = () => null;
		};
	}, []);

	const submit = () => formRef.current?.submit();

	const onSubmit = async (values: any) => {
		const form = formRef.current;
		if (form === null) return;

		const informationCount = values?.informations?.length ?? 0;

		if (informationCount === 0) {
			const message = txt('oplevalt.notifications.infomation required'); // !
			return notification.error({ message });
		}

		const imageCount = values?.images?.length ?? 0;

		if (imageCount === 0) {
			const message = txt('oplevalt.notifications.image required'); // !
			return notification.error({ message });
		}

		if (values?.variations?.length === 1 && values.variations[0].title !== values.title) {
			const message =txt('oplevalt.notifications.variation title wrong'); // !
			return notification.error({ message });
		}

		try {
			if (experienceId === newExperienceSymbol) {
				const experienceId = await ExperienceService.create(values);
				const message = txt('oplevalt.notifications.create successful');
				notification.success({ message });
				return history.push('/oplevalt/experiences/' + experienceId);
			}

			if (experienceId !== newExperienceSymbol) {
				await ExperienceService.update(Number(experienceId), values);
				const message = txt('oplevalt.notifications.update successful');
				notification.success({ message });
				setFormChanged(false);
			}
		} catch (error) {
			if ((error as Error).message === 'url exists') {
				const message = txt('oplevalt.notifications.url exists');
				form.setFields([{ name: 'url', errors: [message] }]);
				notification.warn({ message });
			}

			if (experienceId === newExperienceSymbol) {
				const message = txt('oplevalt.notifications.create failed');
				notification.error({ message });
			}

			if (experienceId !== newExperienceSymbol) {
				const message = txt('oplevalt.notifications.update failed');
				notification.error({ message });
			}
		}
	};

	const onFormChange = (changedValues: any, a: any) => {
		setFormChanged(true);
		if ('title' in changedValues) setExperienceTitle(changedValues.title);
	};

	const openPreview = () => {
		const form = formRef.current;
		if (form === null) return;

		const url = form.getFieldValue('url');
		const released = form.getFieldValue('released');
		const query = released ? '' : '?preview';

		const oplevaltHost = process.env.REACT_APP_OPLEVALT_HOST;
		window.open(oplevaltHost + '/oplevelser/' + url + query, '_blank');
	};

	const deleteExperience = async () => {
		await ExperienceService.remove(Number(experienceId));
		history.replace('/oplevalt/experiences');
	};

	return (
		<MainStyled>
			<div className="header-container row-container">
				<Typography.Text
					strong={experienceTitle !== ''}
					italic={experienceTitle === ''}
					type="secondary"
					className="header-title"
				>
					{experienceTitle || txt('oplevalt.title placeholder')}
				</Typography.Text>

				<Tooltip
					title={
						experienceId === newExperienceSymbol
							? txt('oplevalt.create')
							: txt('oplevalt.update')
					}
				>
					<Badge
						offset={[-10, 10]}
						count={
							formChanged ? (
								<FontAwesomeIcon
									className="unsaved-icon"
									icon={faCircleExclamation}
								/>
							) : null
						}
					>
						<Button
							type="link"
							loading={isLoading}
							disabled={isLoading}
							onClick={submit}
						>
							<FontAwesomeIcon icon={faCloudCheck} />
						</Button>
					</Badge>
				</Tooltip>

				<Tooltip title={txt('global.open')}>
					<Button type="link" onClick={openPreview}>
						<FontAwesomeIcon icon={faArrowUpRightFromSquare} />
					</Button>
				</Tooltip>

				<Tooltip title={txt('global.images')}>
					<Button type="link" onClick={() => setOpenHeaderImageCatalogue(true)}>
						<FontAwesomeIcon icon={faFolderImage} />
					</Button>
				</Tooltip>

				<Divider type="vertical" />

				<Tooltip title={txt('oplevalt.delete')}>
					<Button
						type="link"
						onClick={() => setShowDeleteModal(true)}
						loading={isLoading}
						disabled={isLoading}
						danger
					>
						<FontAwesomeIcon icon={faTrash} />
					</Button>
				</Tooltip>
			</div>

			<Form
				ref={formRef}
				onFinish={onSubmit}
				onValuesChange={onFormChange}
				layout="vertical"
				requiredMark={false}
				initialValues={{ informations: [{}], variations: [{}]}}
			>
				<section>
					<Typography.Title level={4} type="secondary">
						{txt('oplevalt.general header')}
					</Typography.Title>

					<Form.Item
						label={txt('oplevalt.title')}
						name="title"
						rules={[{ required: true, message: txt('global.required') }]}
					>
						<Input placeholder="Aa" size="large" />
					</Form.Item>

					<div className="row-container stretch-container">
						<Form.Item
							name="subtitle"
							label={txt('oplevalt.subtitle')}
							rules={[{ required: true, message: txt('global.required') }]}
						>
							<Input placeholder="Aa" />
						</Form.Item>

						<Form.Item
							name="url"
							label={txt('oplevalt.url')}
							rules={[
								{
									required: true,
									pattern: /[a-z]+/gm,
									message: txt('global.required'),
								},
							]}
						>
							<Input placeholder="Aa" />
						</Form.Item>
					</div>

					<Form.Item
						name="teaser"
						label={txt('oplevalt.teaser')}
						rules={[{ required: true, message: txt('global.required') }]}
					>
						<Input.TextArea showCount placeholder="Aa" />
					</Form.Item>

					<div className="row-container centered-content">
						<Form.Item
							name="released"
							label={txt('oplevalt.released')}
							valuePropName="checked"
							initialValue={false}
						>
							<Switch />
						</Form.Item>

						<Form.Item
							name="giftCard"
							label={txt('oplevalt.gift card')}
							valuePropName="checked"
							initialValue={true}
						>
							<Switch />
						</Form.Item>
					</div>
				</section>

				<section>
					<Typography.Title level={4} type="secondary">
						{txt('oplevalt.meta')}
					</Typography.Title>

					<Form.Item
						label={txt('oplevalt.title')}
						name={['meta', 'title']}
						rules={[{ required: true, message: txt('global.required') }]}
					>
						<Input placeholder="Aa" />
					</Form.Item>

					<Form.Item
						name={['meta', 'description']}
						label={txt('oplevalt.description')}
						rules={[{ required: true, message: txt('global.required') }]}
					>
						<Input.TextArea showCount placeholder="Aa" />
					</Form.Item>
				</section>

				<section>
					<Typography.Title level={4} type="secondary">
						{txt('oplevalt.categories')}
					</Typography.Title>

					<Form.Item
						label={txt('oplevalt.provinces')}
						name='provinces'
						rules={[{ required: true, message: txt('global.required') }]}
					>
						<Select
							mode="multiple"
							placeholder={txt('oplevalt.choose province')}
						>
							{provinces.map((province) => (
								<Select.Option key={province.id} value={province.id}>{province.name}</Select.Option>
							))}
						</Select>
					</Form.Item>

					<Form.Item
						label={txt('oplevalt.categories')}
						name='categories'
						rules={[{ required: true, message: txt('global.required') }]}
					>
						<Select
							mode="multiple"
							placeholder={txt('oplevalt.choose categories')}
						>
							{categories.map((c) => (
								<Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>
							))}
						</Select>
					</Form.Item>
				</section>

				<section>
					<Typography.Title level={4} type="secondary">
						{txt('oplevalt.variations')}
					</Typography.Title>

					<Form.List name="variations">
						{(variations, variationActions) => (
							<div>
								{variations.map((variation) => (
									<div key={variation.key} className="variation-container">
										<div>
											<Form.Item
												{...variation}
												name={[variation.name, 'title']}
												fieldKey={[variation.fieldKey, 'title']}
												initialValue={formRef?.current?.getFieldValue('title')}
												rules={[
													{
														required: true,
														message: txt('global.required'),
													},
												]}
											>
												<Input placeholder={txt('oplevalt.variation placeholder')} />
											</Form.Item>

											<Typography.Title level={5} type="secondary">
												{txt('oplevalt.price')}
											</Typography.Title>

											<div className="centered-content-container">
												<Form.Item
													name={[variation.name, 'price']}
													label={txt('oplevalt.price')}
													rules={[
														{
															required: true,
															message: txt('global.required'),
														},
													]}
												>
													<InputNumber />
												</Form.Item>

												<Form.Item
													name={[variation.name, 'priceIndicative']}
													label={txt('oplevalt.price indicative')}
													rules={[
														{
															required: true,
															message: txt('global.required'),
														},
													]}
												>
													<InputNumber />
												</Form.Item>

												<Form.Item
													initialValue={0}
													name={[variation.name, 'minimumAmount']}
													label={txt('oplevalt.minimum amount')}
													rules={[
														{
															required: true,
															message: txt('global.required'),
														},
													]}
												>
													<InputNumber />
												</Form.Item>

												<Form.Item
													initialValue={0}
													name={[variation.name, 'maximumAmount']}
													label={txt('oplevalt.maximum amount')}
													rules={[
														{
															required: true,
															message: txt('global.required'),
														},
													]}
												>
													<InputNumber />
												</Form.Item>

												<Form.Item
													name={[variation.name, 'vat']}
													label={txt('oplevalt.vat')}
													valuePropName="checked"
													initialValue={true}
												>
													<Switch />
												</Form.Item>

												{variations.length > 1 && (
													<Tooltip title={txt('oplevalt.delete')}>
														<Button
															danger
															type="primary"
															className="delete-button"
															onClick={() => variationActions.remove(variation.name)}
														>
															<FontAwesomeIcon icon={faTrash}/>
														</Button>
													</Tooltip>
												)}

											</div>
										</div>

										<Form.List name={[variation.name, 'counterTypes']}>
											{(counterTypes, counterTypeActions) => (
												<div className="column-container counters-container">
													{counterTypes.map((counterType) => (
														<div
															key={counterType.key}
															className="column-container counter-container"
														>

															<Typography.Title level={5} type="secondary">
																{txt('oplevalt.counter')}
															</Typography.Title>

															<div className="row-container">
																<Form.Item
																	{...counterType}
																	fieldKey={[counterType.fieldKey, 'type']}
																	name={[counterType.name, 'type']}
																	label={txt('oplevalt.type')}
																	rules={[
																		{
																			required: true,
																			message: txt('global.required'),
																		},
																	]}
																>
																	<Select placeholder={txt('oplevalt.types.select')}>
																		<Select.Option value="adultCount">
																			{txt('oplevalt.types.adults')}
																		</Select.Option>

																		<Select.Option value="childCount">
																			{txt('oplevalt.types.children')}
																		</Select.Option>

																		<Select.Option value="packageCount">
																			{txt('oplevalt.types.package')}
																		</Select.Option>

																		<Select.Option value="extraTimeCount">
																			{txt('oplevalt.types.extraTime')}
																		</Select.Option>
																	</Select>
																</Form.Item>

																<Form.Item
																	{...counterType}
																	fieldKey={[counterType.fieldKey, 'description']}
																	name={[counterType.name, 'description']}
																	label={txt('oplevalt.description')}
																	rules={[
																		{
																			required: true,
																			message: txt('global.required'),
																		},
																	]}
																>
																	<Input placeholder="Aa" />
																</Form.Item>

																<Form.Item
																	{...counterType}
																	initialValue={0}
																	fieldKey={[counterType.fieldKey, 'price']}
																	name={[counterType.name, 'price']}
																	label={txt('oplevalt.price')}
																	rules={[
																		{
																			required: true,
																			message: txt('global.required'),
																		},
																	]}
																>
																	<InputNumber />
																</Form.Item>

																<Form.Item
																	{...counterType}
																	initialValue={0}
																	fieldKey={[counterType.fieldKey, 'priceIndicative']}
																	name={[counterType.name, 'priceIndicative']}
																	label={txt('oplevalt.price indicative')}
																	rules={[
																		{
																			required: true,
																			message: txt('global.required'),
																		},
																	]}
																>
																	<InputNumber />
																</Form.Item>
															</div>

															<div className="row-container">
																<Form.Item
																	{...counterType}
																	initialValue={0}
																	fieldKey={[counterType.fieldKey, 'minimumAmount']}
																	name={[counterType.name, 'minimumAmount']}
																	label={txt('oplevalt.minimum amount')}
																	rules={[
																		{
																			required: true,
																			message: txt('global.required'),
																		},
																	]}
																>
																	<InputNumber />
																</Form.Item>

																<Form.Item
																	{...counterType}
																	initialValue={0}
																	fieldKey={[counterType.fieldKey, 'maximumAmount']}
																	name={[counterType.name, 'maximumAmount']}
																	label={txt('oplevalt.maximum amount')}
																	rules={[
																		{
																			required: true,
																			message: txt('global.required'),
																		},
																	]}
																>
																	<InputNumber />
																</Form.Item>

																<Form.Item
																	{...counterType}
																	fieldKey={[counterType.fieldKey, 'defaultAmount']}
																	name={[counterType.name, 'defaultAmount']}
																	label={txt('oplevalt.start amount')}
																	initialValue={0}
																	rules={[
																		{
																			required: true,
																			message: txt('global.required'),
																		},
																	]}
																>
																	<InputNumber />
																</Form.Item>

																<Tooltip title={txt('oplevalt.delete')}>
																	<Button
																		danger
																		type="primary"
																		className="delete-button"
																		onClick={() => counterTypeActions.remove(counterType.name)}
																	>
																		<FontAwesomeIcon icon={faTrash}/>
																	</Button>
																</Tooltip>
															</div>
														</div>
													))}

													<Tooltip title={txt('oplevalt.add counter')}>
														<Button
															block
															type="dashed"
															onClick={() => counterTypeActions.add()}
														>
															<FontAwesomeIcon icon={faPlus} />
															{txt('oplevalt.add counter')}
														</Button>
													</Tooltip>
												</div>
											)}
										</Form.List>


										{[4, 28].includes(Number(experienceId)) && (
											<Form.List name={[variation.name, 'availableDates']}>
												{(availableDates, availableDateActions) => (
													<div className="column-container counters-container">

														<Typography.Title level={5} type="secondary">
															{txt('oplevalt.available dates')}
														</Typography.Title>

														{availableDates.map((availableDate) => (
																<div key={availableDate.key} className="row-container">

																	<Form.Item
																		{...availableDate}
																		fieldKey={[availableDate.fieldKey, 'value']}
																		name={[availableDate.name, 'value']}
																		rules={[{ required: true, message: txt('global.required') }]}
																		noStyle
																	>
																		<DatePicker showTime showSecond={false} minuteStep={5} format="dddd \d. DD MMMM YYYY HH:mm" />
																	</Form.Item>
																	<Form.Item
																		{...availableDate}
																		initialValue={crypto.randomUUID().toUpperCase()}
																		fieldKey={[availableDate.fieldKey, 'id']}
																		name={[availableDate.name, 'id']}
																		noStyle
																	>
																		<Input disabled bordered={false} style={{flex: 1}} />
																	</Form.Item>

																	<Tooltip title={txt('oplevalt.delete')}>
																		<Button
																			danger
																			type="primary"
																			className="delete-button"
																			onClick={() => availableDateActions.remove(availableDate.name)}
																		>
																			<FontAwesomeIcon icon={faTrash}/>
																		</Button>
																	</Tooltip>
																</div>
														))}

														<Tooltip title={txt('oplevalt.add counter')}>
															<Button
																block
																type="dashed"
																onClick={() => availableDateActions.add()}
															>
																<FontAwesomeIcon icon={faPlus} />
																{txt('oplevalt.add available date')}
															</Button>
														</Tooltip>
													</div>
												)}
											</Form.List>
										)}

									</div>
								))}

								<Form.Item className="add-variation">
									<Tooltip title={txt('oplevalt.add variation')}>
										<Button block type="dashed" onClick={() => variationActions.add()}>
											<FontAwesomeIcon icon={faPlus} />
											{txt('oplevalt.add variation')}
										</Button>
									</Tooltip>
								</Form.Item>
							</div>
						)}
					</Form.List>
				</section>

				<section>
					<Typography.Title level={4} type="secondary">
						{txt('oplevalt.price')}
					</Typography.Title>

					<div className="centered-content-container">
						<Form.Item
							name="price"
							label={txt('oplevalt.price')}
							rules={[{ required: true, message: txt('global.required') }]}
						>
							<InputNumber />
						</Form.Item>

						<Form.Item
							name="priceIndicative"
							label={txt('oplevalt.price indicative')}
							rules={[{ required: true, message: txt('global.required') }]}
						>
							<InputNumber />
						</Form.Item>

						<Form.Item
							initialValue={0}
							name="minimumAmount"
							label={txt('oplevalt.minimum amount')}
							rules={[
								{
									required: true,
									message: txt('global.required'),
								},
							]}
						>
							<InputNumber />
						</Form.Item>

						<Form.Item
							initialValue={0}
							name="maximumAmount"
							label={txt('oplevalt.maximum amount')}
							rules={[
								{
									required: true,
									message: txt('global.required'),
								},
							]}
						>
							<InputNumber />
						</Form.Item>

						<Form.Item
							name="vat"
							label={txt('oplevalt.vat')}
							valuePropName="checked"
							initialValue={true}
						>
							<Switch />
						</Form.Item>
					</div>

					<Typography.Title level={4} type="secondary">
						{txt('oplevalt.counter')}
					</Typography.Title>

					<Form.List name="counterTypes">
						{(counters, { add, remove }) => (
							<div className="column-container counters-container">
								{counters.map(({ key, name, ...restField }) => (
									<div key={key} className="column-container counter-container">
										<div className="row-container">
											<Form.Item
												{...restField}
												name={[name, 'type']}
												label={txt('oplevalt.type')}
												rules={[
													{
														required: true,
														message: txt('global.required'),
													},
												]}
											>
												<Select placeholder={txt('oplevalt.types.select')}>
													<Select.Option value="adultCount">
														{txt('oplevalt.types.adults')}
													</Select.Option>

													<Select.Option value="childCount">
														{txt('oplevalt.types.children')}
													</Select.Option>

													<Select.Option value="packageCount">
														{txt('oplevalt.types.package')}
													</Select.Option>

													<Select.Option value="extraTimeCount">
														{txt('oplevalt.types.extraTime')}
													</Select.Option>
												</Select>
											</Form.Item>

											<Form.Item
												{...restField}
												name={[name, 'description']}
												label={txt('oplevalt.description')}
												rules={[
													{
														required: true,
														message: txt('global.required'),
													},
												]}
											>
												<Input placeholder="Aa" />
											</Form.Item>

											<Form.Item
												{...restField}
												initialValue={0}
												name={[name, 'price']}
												label={txt('oplevalt.price')}
												rules={[
													{
														required: true,
														message: txt('global.required'),
													},
												]}
											>
												<InputNumber />
											</Form.Item>

											<Form.Item
												{...restField}
												initialValue={0}
												name={[name, 'priceIndicative']}
												label={txt('oplevalt.price indicative')}
												rules={[
													{
														required: true,
														message: txt('global.required'),
													},
												]}
											>
												<InputNumber />
											</Form.Item>
										</div>

										<div className="row-container">
											<Form.Item
												{...restField}
												initialValue={0}
												name={[name, 'minimumAmount']}
												label={txt('oplevalt.minimum amount')}
												rules={[
													{
														required: true,
														message: txt('global.required'),
													},
												]}
											>
												<InputNumber />
											</Form.Item>

											<Form.Item
												{...restField}
												initialValue={0}
												name={[name, 'maximumAmount']}
												label={txt('oplevalt.maximum amount')}
												rules={[
													{
														required: true,
														message: txt('global.required'),
													},
												]}
											>
												<InputNumber />
											</Form.Item>

											<Form.Item
												{...restField}
												name={[name, 'defaultAmount']}
												label={txt('oplevalt.start amount')}
												initialValue={0}
												rules={[
													{
														required: true,
														message: txt('global.required'),
													},
												]}
											>
												<InputNumber />
											</Form.Item>

											<Tooltip title={txt('oplevalt.delete')}>
												<Button
													danger
													type="primary"
													className="delete-button"
													onClick={() => remove(name)}
												>
													<FontAwesomeIcon icon={faTrash} />
												</Button>
											</Tooltip>
										</div>
									</div>
								))}

								<Tooltip title={txt('oplevalt.add')}>
									<Button block type="dashed" onClick={() => add()}>
										<FontAwesomeIcon icon={faPlus} />
									</Button>
								</Tooltip>
							</div>
						)}
					</Form.List>
				</section>

				<section>
					<Typography.Title level={4} type="secondary">
						{txt('oplevalt.add-ons')}
					</Typography.Title>

					<Form.List name="addOns">
						{(addOns, { add, remove }) => (
							<div className="row-container add-ons-container">
								{addOns.map(({ key, name, ...restField }) => {
									const form = formRef.current;
									const fieldValue = form?.getFieldValue(['addOns', name]);

									return (
										<div key={key} className="add-on-container">
											<div className="image-container">
												<Form.Item
													{...restField}
													name={[name, 'image']}
													noStyle
												>
													<Input
														className="hidden-input"
														placeholder="Aa"
													/>
												</Form.Item>

												<img
													draggable={false}
													className="image-preview"
													src={`${OplevaltContext.imageSourcePrefix}${fieldValue?.image}`}
													alt="preview"
												/>
											</div>

											<div className="add-on-info">
												<Form.Item
													{...restField}
													name={[name, 'title']}
													label={txt('oplevalt.title')}
													rules={[
														{
															required: true,
															message: txt('global.required'),
														},
													]}
												>
													<Input placeholder="Aa" />
												</Form.Item>

												<Form.Item
													{...restField}
													name={[name, 'ordertext']}
													label={txt('oplevalt.cart')}
													rules={[
														{
															required: true,
															message: txt('global.required'),
														},
													]}
												>
													<Input placeholder="Aa" />
												</Form.Item>

												<Form.Item
													{...restField}
													name={[name, 'info']}
													label={txt('oplevalt.information')}
												>
													<Input.TextArea placeholder="Aa" />
												</Form.Item>

												<div className="row-container">
													<Form.Item
														{...restField}
														name={[name, 'amount']}
														label={txt('oplevalt.price')}
														initialValue={0}
													>
														<InputNumber />
													</Form.Item>

													<Tooltip title={txt('oplevalt.delete')}>
														<Button
															danger
															type="primary"
															className="delete-button"
															onClick={() => remove(name)}
														>
															<FontAwesomeIcon icon={faTrash} />
														</Button>
													</Tooltip>
												</div>
											</div>
										</div>
									);
								})}

								<ImageCatalogue
									useVisible={[
										openAddOnImageCatalogue,
										setOpenAddOnImageCatalogue,
									]}
									sourcePrefix={OplevaltContext.imageSourcePrefix}
									onQuery={ImageService.query}
									onCreate={ImageService.create}
									onRemove={ImageService.remove}
									onSelectImage={(imageName) => add({ image: imageName })}
								/>
							</div>
						)}
					</Form.List>

					<Tooltip title={txt('oplevalt.add')}>
						<Button
							block
							type="dashed"
							onClick={() => setOpenAddOnImageCatalogue(true)}
						>
							<FontAwesomeIcon icon={faPlus} />
						</Button>
					</Tooltip>
				</section>

				<section>
					<Typography.Title level={4} type="secondary">
						{txt('oplevalt.informations')}
					</Typography.Title>

					<Form.List name="informations">
						{(informations, { add, remove }) => (
							<div className="informations-container">
								{informations.map(({ key, name, ...restField }) => (
									<div
										key={key}
										className="column-container information-container"
									>
										<Form.Item
											{...restField}
											name={[name, 'title']}
											label={txt('oplevalt.title')}
											rules={[
												{ required: true, message: txt('global.required') },
											]}
										>
											<Input placeholder="Aa" />
										</Form.Item>

										<Form.Item
											{...restField}
											name={[name, 'content']}
											label={txt('oplevalt.content')}
											rules={[
												{ required: true, message: txt('global.required') },
											]}
										>
											<Input.TextArea
												placeholder="Aa"
												className="text-multiline-container"
											/>
										</Form.Item>

										{informations.length > 1 && (
											<Tooltip title={txt('oplevalt.delete')}>
												<Button
													danger
													type="primary"
													className="delete-button"
													onClick={() => remove(name)}
												>
													<FontAwesomeIcon icon={faTrash} />
												</Button>
											</Tooltip>
										)}
									</div>
								))}

								<Tooltip title={txt('oplevalt.add')}>
									<Button block type="dashed" onClick={() => add()}>
										<FontAwesomeIcon icon={faPlus} />
									</Button>
								</Tooltip>
							</div>
						)}
					</Form.List>
				</section>

				<section>
					<Typography.Title level={4} type="secondary">
						{txt('oplevalt.images')}
					</Typography.Title>

					<Form.List name="images">
						{(images, { add, remove }) => (
							<div className="images-container row-container">
								{images.map(({ key, name, ...restField }) => {
									const form = formRef.current;
									const fieldValue = form?.getFieldValue(['images', name]);

									return (
										<div key={key} className="image-container">
											<Form.Item {...restField} name={name} noStyle>
												<Input className="hidden-input" placeholder="Aa" />
											</Form.Item>

											<img
												alt="preview"
												className="image-preview"
												src={`${OplevaltContext.imageSourcePrefix}${fieldValue}`}
											/>

											<Tooltip title={txt('oplevalt.delete')}>
												<Button
													danger
													type="primary"
													className="delete-button"
													onClick={() => remove(name)}
												>
													<FontAwesomeIcon icon={faTrash} />
												</Button>
											</Tooltip>
										</div>
									);
								})}

								<ImageCatalogue
									useVisible={[
										openImageImageCatalogue,
										setOpenImageImageCatalogue,
									]}
									sourcePrefix={OplevaltContext.imageSourcePrefix}
									onQuery={ImageService.query}
									onCreate={ImageService.create}
									onRemove={ImageService.remove}
									onSelectImage={(imageName) => add(imageName)}
								/>
							</div>
						)}
					</Form.List>

					<Tooltip title={txt('oplevalt.add')}>
						<Button
							block
							type="dashed"
							onClick={() => setOpenImageImageCatalogue(true)}
						>
							<FontAwesomeIcon icon={faPlus} />
						</Button>
					</Tooltip>
				</section>
			</Form>

			<ImageCatalogue
				useVisible={[openHeaderImageCatalogue, setOpenHeaderImageCatalogue]}
				sourcePrefix={OplevaltContext.imageSourcePrefix}
				onQuery={ImageService.query}
				onCreate={ImageService.create}
				onRemove={ImageService.remove}
			/>

			<DeleteModal
				onConfirm={deleteExperience}
				useVisible={[showDeleteModal, setShowDeleteModal]}
			/>
		</MainStyled>
	);
};

const MainStyled = styled.main`
	background-color: ${OplevaltContext.theme.orange.neutral};
	display: flex;
	flex-direction: column;
	align-items: center;
	overflow: auto;
	position: relative;

	.ant-switch-checked {
		background-color: ${OplevaltContext.theme.orange.vibrant};
	}

	form {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		width: 100%;
		max-width: 800px;
		margin: 1rem;
	}

	section {
		display: flex;
		flex-direction: column;
		background-color: white;
		padding: 1rem 1.4rem;
		border-radius: 1rem;
	}

	textarea {
		resize: vertical;
		height: 12rem;
	}

	.row-container {
		display: flex;
		gap: 1rem;
		flex: 1;
	}

	.column-container {
		display: flex;
		flex-direction: column;
	}

	.centered-content {
		justify-content: center;
		gap: 1rem;
	}

	.flew-wrap-container {
		display: flex;
		flex-wrap: wrap;
		gap: 1rem;
		align-items: center;
		justify-content: center;
	}

	.stretch-container {
		* {
			flex: 1;
		}
	}

	input.hidden-input {
		display: none;
	}

	img.image-preview {
		background-color: #fafafa;

		border-radius: 1rem;
		height: 12rem;
		aspect-ratio: 1;
		object-fit: cover;
	}

	button.delete-button {
		width: 100%;
		max-width: 92px;
		align-self: center;
	}

	.text-multiline-container {
		min-height: 8rem;
	}

	.images-container {
		padding-block: 1rem;
		overflow-x: scroll;

		.image-container {
			img {
				border: 4px dotted transparent;
			}

			&:first-child {
				img {
					border-color: ${OplevaltContext.theme.orange.vibrant};
				}
			}
		}
	}

	.image-container {
		position: relative;

		.delete-button {
			position: absolute;
			right: 0;
			transform: translate(30%, -30%);
			width: min-content;
			opacity: 0;
			transition: opacity 350ms;
			z-index: 1;
		}

		&:hover {
			.delete-button {
				opacity: 1;
			}
		}
	}

	.header-container {
		background-color: white;
		position: sticky;
		top: 0;
		width: 100%;
		padding: 0.4rem 1rem;
		z-index: 1;
		box-shadow: 0 0 1rem #ddd9;

		display: flex;
		align-items: center;

		.header-title {
			margin-right: auto;
		}
	}

	.add-ons-container {
		padding: 1rem;
		overflow-x: scroll;
	}

	.add-on-container {
		display: flex;
		flex-direction: column;
		width: min-content;
		align-items: center;
		gap: 1rem;

		box-shadow: 0 0 1rem #9993;
		border-radius: 1rem;

		.add-on-info {
			padding-inline: 1rem;
		}
	}

	.informations-container {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.information-container {
		box-shadow: 0 0 1rem #9993;
		border-radius: 1rem;
		padding: 1rem;
	}

	.counters-container {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	.centered-content-container {
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 1rem;
	}

	.counter-container {
		box-shadow: 0 0 1rem #9993;
		border-radius: 1rem;
		padding: 1rem;
		align-items: center;
	}

	.label-switch-container {
		display: flex;
		align-items: center;
		gap: 0.4rem;
	}

	.unsaved-icon {
		color: ${OplevaltContext.theme.orange.vibrant};
	}
	
	.variation-container {
		display: flex;
		flex-direction: column;
		gap: 1rem;
		padding: 2rem ;
		margin: 0 0 1rem 0;
		box-shadow: 0 0 1rem #9993;
		border-radius: 1rem;
	}

	.add-variation {
		padding: 1rem 0;
	}
`;
